import {
  Configuration,
  RequestOpts,
  RequestContext,
  ResponseContext,
  Middleware,
  ShoppingDiagnosticsApi,
  UserPermissionsApi,
  GlobalFilterRuleListApi,
  GlobalFilterRuleFormApi,
  IdentitySettingsListApi,
  IdentitySettingsFormApi,
  UpdateHistoryApi,
  SupplierLocationMappingListApi,
  SupplierLocationMappingFormApi,
  AncillarySupplierListApi,
  AncillarySupplierFormApi,
  AncillaryDistributionListApi,
  AncillaryDistributionFormApi,
  AncillaryDistributionCarrierBySupplierAutocompleteApi,
  SearchRuleListApi,
  SearchRuleFormApi,
  VccCredentialsListApi,
  VccCredentialsFormApi,
  UpdateHistoryV2Api,
} from '../integrated-client';
import {
  SMRuleListApi,
  SMRuleFormApi,
  SMSearchFormApi,
  BCMOsisRuleListApi,
  BCMOsisRuleFormApi,
  BCMBookingIdRemarkRuleFormApi,
  BCMBookingIdRemarkRuleListApi,
  AgencyAutocompleteApi,
  AirlineAutocompleteApi,
  StationLocationAutocompleteApi,
  LocationAutocompleteApi,
  CountryAutocompleteApi,
  GeographyApi,
  StationsApi,
  AirlinesApi,
  BrandListApi,
  BrandFormApi,
  MarketListApi,
  MarketFormApi,
  DistributionChannelFormApi,
  DistributionChannelListApi,
  AgencyFormApi,
  AgencyListApi,
  AgencyGroupFormApi,
  AgencyGroupListApi,
  MarkupsApi,
  PreferenceRuleApi,
  ProviderCredentialFormApi,
  ProviderCredentialListApi,
  TicketTimeLimitOverrideApi,
  ProviderListApi,
  ProviderFormApi,
  AgencyApi,
  IdentityProviderSettingsFormApi,
  IdentityProviderSettingsListApi,
  SSRRuleListApi,
  SSRRuleFormApi,
  OsisRuleListApi,
  OsisRuleFormApi,
  BookingTolerancesListApi,
  SearchSubRuleFormApi,
  BookingTolerancesFormApi,
  AncillaryManagerListApi,
  AncillaryManagerFormApi,
  AutoCompleteApi,
  BusinessDataApi,
  MarkupApi,
  TTLOV2ListApi,
  TTLOV2FormApi,
  UserPermissionsBrandAndMarketAutoCompleteApi,
  MarkupV2ListApi,
  MarkupsV2FormApi
} from '../integrated-client/apis';
import applicationStore from '../store/ApplicationStore';
import { RequestActions } from '../store/actions/requestActions';
import { NotificationActions } from '../store/actions/notificationActions';
import { NotificationType } from '../views/NotificationsManager/Notification/Notification';
import { BookingIdRemarkRuleFormApi } from '../integrated-client';
import { BookingIdRemarkRuleListApi } from '../integrated-client';
import { DistributionChannelApi } from '../integrated-client';

const cancelableUrls = [
  '/api/StationLocationAutocomplete',
  '/api/AirlineAutcomplete',
  '/api/AgencyAutocomplete',
  '/api/CountryAutocomplete',
  '/api/LocationAutocomplete',
];

const suppressServerNotificationUrls = [
  '/api/ShoppingDiagnostics/Shopping',
  '/api/ShoppingDiagnostics/GetDiagnosticsData',
  '/api/ShoppingDiagnostics/GetDiagnosticsDataNew',
];

export enum HandledErrorType {
  HANDLED = 'Error handled',
  UNHANDLED = 'Unhandled server error',
  NOTIFICATION = 'Nofitication',
}

class RestMiddleware implements Middleware {
  pre(context: RequestContext) {
    cancelableUrls.forEach((request) => {
      if (context.url.indexOf(request) > -1) {
        /* cancel prev requests */
        RequestActions.getStartedRequests().requestsInProgress.requests.forEach((r: any) => {
          if (!r.abortController.signal.aborted) {
            r.abortController.abort();
          }
        });
      }
    });
    const abortController: AbortController = new AbortController();
    RequestActions.requestStarted(abortController);
    (context.init as any)._abortController = abortController;

    let newContext = {
      url: context.url,
      init: { ...context.init, signal: abortController.signal },
    };
    return Promise.resolve(newContext);
  }

  post(context: ResponseContext) {
    RequestActions.requestStopped((context.init as any)._abortController);
    return Promise.resolve(context.response);
  }
}

let hasOpenNotificationPopup = false;

type Constructor<T> = new (...args: any[]) => T;

function withRequestHandler<T extends Constructor<{}>>(Base: T, localErrors = false) {
  return class extends Base {
    bearerToken: string | null;

    constructor(...args: any[]) {
      super(...args);
      this.bearerToken = null;
    }

    getBearerTokenFromStore() {
      const store = applicationStore.getState();
      this.bearerToken = store.authData.authManager?.getAccessToken();
    }

    hasToken() {
      return localStorage.getItem('msal.idtoken') !== null;
    }

    withBearerToken(context: RequestOpts) {
      if (this.bearerToken) {
        const bearer = `Bearer ${this.bearerToken}`;
        if (!context?.headers) {
          context['headers'] = { Authorization: bearer };
        } else {
          context.headers['Authorization'] = bearer;
        }
      }
      return context;
    }

    protected async handleError(response: any, url: string): Promise<HandledErrorType> {
      return new Promise((resolve, reject) => {
        response.json().then((json: any) => {
          const { status } = json;
          if (status != null && !hasOpenNotificationPopup) {
            hasOpenNotificationPopup = true;
            const correlationId = response.headers.get('x-correlation-id');
            const details = correlationId ? `contextId: ${correlationId}` : '';
            NotificationActions.addNotification({
              type: NotificationType.CLIENT_ERROR,
              message: `An Internal Error has occured.`,
              details,
              buttons: [
                {
                  caption: 'OK',
                  action: () => {
                    hasOpenNotificationPopup = false;
                    return Promise.resolve();
                  },
                },
              ],
            });
            resolve(HandledErrorType.NOTIFICATION);
          } else {
            resolve(HandledErrorType.UNHANDLED);
          }
        });
      });
    }

    protected async request(context: RequestOpts): Promise<Response> {
      const correlationId = localErrors ? window.crypto.randomUUID() : undefined;
      if (correlationId) {
        context.headers = { ...context.headers, 'x-correlation-id': correlationId };
      }
      if (!this.bearerToken) {
        this.getBearerTokenFromStore();
      }
      if (!this.hasToken()) {
        if (localErrors) throw Object.assign(new Error(), { status: 401, correlationId });
        window.dispatchEvent(new Event('not_authorized'));
        return Promise.reject();
      }
      let contextWithToken = this.withBearerToken(context);
      const { url, init } = (this as any).createFetchParams(contextWithToken);
      let response,
        aborted = false;

      try {
        response = await (this as any).fetchApi(url, init);
      } catch (e: any) {
        RequestActions.requestStopped((init as any)._abortController);
        if (localErrors) throw Object.assign(new Error(), { status: 0, correlationId });
        if (e.name !== 'AbortError') {
          throw e;
        } else {
          aborted = true;
        }
      }
      if (aborted) {
        return Promise.reject('aborted');
      }

      if (response.status >= 200 && response.status < 300) {
        if (response.status === 204) {
          response.json = () => {
            return {};
          };
        }
        return response;
      }

      if (localErrors) {
        RequestActions.requestStopped((init as any)._abortController);
        let body: any = {};
        try {
          body = await response.clone().json();
        } catch {}
        throw Object.assign(new Error(body.message || body.detail), {
          status: response.status,
          correlationId: response.headers.get('x-correlation-id') || body.correlationId || correlationId,
        });
      }

      if (response.status === 401 || response.status === 403) {
        if (!hasOpenNotificationPopup) {
          hasOpenNotificationPopup = true;

          const closePopupHandler = () => {
            hasOpenNotificationPopup = false;
            return Promise.resolve(window.dispatchEvent(new Event('not_authorized')));
          };

          NotificationActions.addNotification({
            type: NotificationType.ERROR,
            title: 'Session expired',
            message: `Your session has expired. Please sign in again.`,
            closePopupHandler,
            buttons: [
              {
                caption: 'Sign in',
                action: closePopupHandler,
              },
            ],
          });
        }

        return Promise.reject();
      }

      if (response.status >= 500) {
        const suppressNotification = suppressServerNotificationUrls.some((request) => url.indexOf(request) > -1);
        if (!suppressNotification) {
          const errorType: HandledErrorType = await this.handleError(response, url);
          if (errorType === HandledErrorType.UNHANDLED) {
            return Promise.reject(HandledErrorType.UNHANDLED);
          }
        }
      }

      RequestActions.requestStopped((init as any)._abortController);
      throw response;
    }
  };
}

class RestClientApi {
  searchManager: {
    ruleForm: SMRuleFormApi;
    ruleList: SMRuleListApi;
    searchForm: SMSearchFormApi;
    shopping: ShoppingDiagnosticsApi;
  };
  searchManagerV2: {
    form: SearchRuleFormApi;
    list: SearchRuleListApi;
    subRuleForm: SearchSubRuleFormApi;
    shopping: ShoppingDiagnosticsApi;
  };
  ttloV2: {
    list: TTLOV2ListApi;
    form: TTLOV2FormApi;
  };
  bookingCreateManager: {
    ruleForm: BCMOsisRuleFormApi;
    ruleList: BCMOsisRuleListApi;
  };
  bookingCreateManagerV2: {
    list: OsisRuleListApi;
    form: OsisRuleFormApi;
  };
  bookingCreateManagerV2Remarks: {
    form: BookingIdRemarkRuleFormApi;
    list: BookingIdRemarkRuleListApi;
  };
  bookingCreateManagerV2SSR: {
    form: SSRRuleFormApi;
    list: SSRRuleListApi;
  };
  bookingCreateManagerRemarks: {
    ruleForm: BCMBookingIdRemarkRuleFormApi;
    ruleList: BCMBookingIdRemarkRuleListApi;
  };
  bookingCreateManagerSSR: {
    list: SSRRuleListApi;
    form: SSRRuleFormApi;
  };
  agencyAutocomplete: AgencyAutocompleteApi;
  airlineAutocomplete: AirlineAutocompleteApi;
  airlineAutoCompleteForAncillaryDistribution: AncillaryDistributionCarrierBySupplierAutocompleteApi;
  locationAutocompleteApi: LocationAutocompleteApi;
  stationLocationAutocompleteApi: StationLocationAutocompleteApi;
  countryAutocomplete: CountryAutocompleteApi;
  autoCompleteV2: AutoCompleteApi;
  userPermissionsBrandAndMarketAutoCompleteApi: UserPermissionsBrandAndMarketAutoCompleteApi;
  geography: GeographyApi;
  stations: StationsApi;
  airlines: AirlinesApi;
  brands: {
    list: BrandListApi;
    form: BrandFormApi;
  };
  markets: {
    list: MarketListApi;
    form: MarketFormApi;
  };
  distributionChannels: {
    list: DistributionChannelListApi;
    form: DistributionChannelFormApi;
  };
  distributionChannelsV2: {
    list: DistributionChannelApi;
  };
  agencies: {
    list: AgencyListApi;
    form: AgencyFormApi;
  };
  agencyGroups: {
    list: AgencyGroupListApi;
    form: AgencyGroupFormApi;
  };
  markups: MarkupsApi;
  preference: PreferenceRuleApi;
  suppliers: {
    credentialsForm: ProviderCredentialFormApi;
    credentialsList: ProviderCredentialListApi;
  };
  providers: {
    form: ProviderFormApi;
    list: ProviderListApi;
  };
  identityProviderSettings: {
    list: IdentityProviderSettingsListApi;
    form: IdentityProviderSettingsFormApi;
  };
  identitySettings: {
    list: IdentitySettingsListApi;
    form: IdentitySettingsFormApi;
  };
  ticketTLO: TicketTimeLimitOverrideApi;
  userPermissions: UserPermissionsApi;
  userPermissionsCopy: UserPermissionsApi;
  agency: AgencyApi;
  globalFilters: {
    form: GlobalFilterRuleFormApi;
    list: GlobalFilterRuleListApi;
  };
  globalFiltersV2: {
    list: GlobalFilterRuleListApi;
    form: GlobalFilterRuleFormApi;
  };
  history: UpdateHistoryApi;
  historyV2: UpdateHistoryV2Api;
  geoGroups: {
    list: SupplierLocationMappingListApi;
    form: SupplierLocationMappingFormApi;
  };
  ancillariesManager: {
    list: AncillaryManagerListApi;
    form: AncillaryManagerFormApi;
  };
  ancillariesMapping: {
    list: AncillarySupplierListApi;
    form: AncillarySupplierFormApi;
  };
  ancillaryDistribution: {
    list: AncillaryDistributionListApi;
    form: AncillaryDistributionFormApi;
  };
  vccCredentials: {
    list: VccCredentialsListApi;
    form: VccCredentialsFormApi;
  };
  bookingTolerances: {
    list: BookingTolerancesListApi;
    form: BookingTolerancesFormApi;
  };
  businessData: {
    data: BusinessDataApi;
  };
  markupV2: {
    data: MarkupApi;
    form: MarkupsV2FormApi;
    editor: MarkupsV2FormApi;
    list: MarkupV2ListApi;
  };
  constructor() {
    const config = new Configuration({
      basePath: window.location.protocol + '//' + window.location.host,
      middleware: [new RestMiddleware()],
    });

    this.searchManager = {
      ruleForm: new (withRequestHandler(SMRuleFormApi))(config),
      ruleList: new (withRequestHandler(SMRuleListApi))(config),
      searchForm: new (withRequestHandler(SMSearchFormApi))(config),
      shopping: new (withRequestHandler(ShoppingDiagnosticsApi))(config),
    };
    this.searchManagerV2 = {
      form: new (withRequestHandler(SearchRuleFormApi))(config),
      list: new (withRequestHandler(SearchRuleListApi))(config),
      subRuleForm: new (withRequestHandler(SearchSubRuleFormApi))(config),
      shopping: new (withRequestHandler(ShoppingDiagnosticsApi))(config),
    };
    this.ttloV2 = {
      list: new (withRequestHandler(TTLOV2ListApi))(config),
      form: new (withRequestHandler(TTLOV2FormApi))(config),
    };
    this.bookingCreateManager = {
      ruleForm: new (withRequestHandler(BCMOsisRuleFormApi))(config),
      ruleList: new (withRequestHandler(BCMOsisRuleListApi))(config),
    };
    this.bookingCreateManagerV2 = {
      list: new (withRequestHandler(OsisRuleListApi))(config),
      form: new (withRequestHandler(OsisRuleFormApi))(config),
    };
    this.bookingCreateManagerV2Remarks = {
      list: new (withRequestHandler(BookingIdRemarkRuleListApi))(config),
      form: new (withRequestHandler(BookingIdRemarkRuleFormApi))(config),
    };
    this.bookingCreateManagerV2SSR = {
      list: new (withRequestHandler(SSRRuleListApi))(config),
      form: new (withRequestHandler(SSRRuleFormApi))(config),
    };
    this.bookingCreateManagerRemarks = {
      ruleForm: new (withRequestHandler(BCMBookingIdRemarkRuleFormApi))(config),
      ruleList: new (withRequestHandler(BCMBookingIdRemarkRuleListApi))(config),
    };
    this.bookingCreateManagerSSR = {
      list: new (withRequestHandler(SSRRuleListApi))(config),
      form: new (withRequestHandler(SSRRuleFormApi))(config),
    };
    this.agencyAutocomplete = new (withRequestHandler(AgencyAutocompleteApi))(config);
    this.airlineAutocomplete = new (withRequestHandler(AirlineAutocompleteApi))(config);
    this.airlineAutoCompleteForAncillaryDistribution = new (withRequestHandler(AncillaryDistributionCarrierBySupplierAutocompleteApi))(config);
    this.locationAutocompleteApi = new (withRequestHandler(LocationAutocompleteApi))(config);
    this.stationLocationAutocompleteApi = new (withRequestHandler(StationLocationAutocompleteApi))(config);
    this.countryAutocomplete = new (withRequestHandler(CountryAutocompleteApi))(config);
    this.autoCompleteV2 = new (withRequestHandler(AutoCompleteApi))(config);
    this.userPermissionsBrandAndMarketAutoCompleteApi = new (withRequestHandler(UserPermissionsBrandAndMarketAutoCompleteApi))(config);
    this.geography = new (withRequestHandler(GeographyApi))(config);
    this.stations = new (withRequestHandler(StationsApi))(config);
    this.airlines = new (withRequestHandler(AirlinesApi))(config);
    this.brands = {
      list: new (withRequestHandler(BrandListApi))(config),
      form: new (withRequestHandler(BrandFormApi))(config),
    };
    this.markets = {
      list: new (withRequestHandler(MarketListApi))(config),
      form: new (withRequestHandler(MarketFormApi))(config),
    };
    this.distributionChannels = {
      list: new (withRequestHandler(DistributionChannelListApi))(config),
      form: new (withRequestHandler(DistributionChannelFormApi))(config),
    };
    this.distributionChannelsV2 = {
      list: new (withRequestHandler(DistributionChannelApi))(config),
    };
    this.agencies = {
      list: new (withRequestHandler(AgencyListApi))(config),
      form: new (withRequestHandler(AgencyFormApi))(config),
    };
    this.agencyGroups = {
      list: new (withRequestHandler(AgencyGroupListApi))(config),
      form: new (withRequestHandler(AgencyGroupFormApi))(config),
    };
    this.markups = new (withRequestHandler(MarkupsApi))(config);
    this.preference = new (withRequestHandler(PreferenceRuleApi))(config);
    this.suppliers = {
      credentialsList: new (withRequestHandler(ProviderCredentialListApi))(config),
      credentialsForm: new (withRequestHandler(ProviderCredentialFormApi))(config),
    };
    this.providers = {
      form: new (withRequestHandler(ProviderFormApi))(config),
      list: new (withRequestHandler(ProviderListApi))(config),
    };
    this.identityProviderSettings = {
      list: new (withRequestHandler(IdentityProviderSettingsListApi))(config),
      form: new (withRequestHandler(IdentityProviderSettingsFormApi))(config),
    };
    this.identitySettings = {
      list: new (withRequestHandler(IdentitySettingsListApi))(config),
      form: new (withRequestHandler(IdentitySettingsFormApi))(config),
    };
    this.ticketTLO = new (withRequestHandler(TicketTimeLimitOverrideApi))(config);
    this.agency = new (withRequestHandler(AgencyApi))(config);
    this.userPermissions = new (withRequestHandler(UserPermissionsApi))(config);
    this.userPermissionsCopy = new (withRequestHandler(UserPermissionsApi, true))(config);
    this.globalFilters = {
      form: new (withRequestHandler(GlobalFilterRuleFormApi))(config),
      list: new (withRequestHandler(GlobalFilterRuleListApi))(config),
    };
    this.globalFiltersV2 = {
      list: new (withRequestHandler(GlobalFilterRuleListApi))(config),
      form: new (withRequestHandler(GlobalFilterRuleFormApi))(config),
    };
    this.history = new (withRequestHandler(UpdateHistoryApi))(config);
    this.historyV2 = new (withRequestHandler(UpdateHistoryV2Api))(config);
    this.geoGroups = {
      list: new (withRequestHandler(SupplierLocationMappingListApi))(config),
      form: new (withRequestHandler(SupplierLocationMappingFormApi))(config),
    };
    this.ancillariesManager = {
      list: new (withRequestHandler(AncillaryManagerListApi))(config),
      form: new (withRequestHandler(AncillaryManagerFormApi))(config),
    };
    this.ancillariesMapping = {
      list: new (withRequestHandler(AncillarySupplierListApi))(config),
      form: new (withRequestHandler(AncillarySupplierFormApi))(config),
    };
    this.ancillaryDistribution = {
      list: new (withRequestHandler(AncillaryDistributionListApi))(config),
      form: new (withRequestHandler(AncillaryDistributionFormApi))(config),
    };
    this.vccCredentials = {
      list: new (withRequestHandler(VccCredentialsListApi))(config),
      form: new (withRequestHandler(VccCredentialsFormApi))(config),
    };
    this.bookingTolerances = {
      list: new (withRequestHandler(BookingTolerancesListApi))(config),
      form: new (withRequestHandler(BookingTolerancesFormApi))(config),
    };
    this.businessData = {
      data: new (withRequestHandler(BusinessDataApi))(config),
    };
    this.markupV2 = {
      data: new (withRequestHandler(MarkupApi))(config),
      form: new (withRequestHandler(MarkupsV2FormApi))(config),
      editor: new (withRequestHandler(MarkupsV2FormApi))(config),
      list: new (withRequestHandler(MarkupV2ListApi))(config),
    };
  }
}

const RestClient: RestClientApi = new RestClientApi();

export default RestClient;
